import { useState, useEffect, useRef } from "react";
import CategoriesComponent from "../Categories/CategoriesComponent";
import Pagination from "@mui/material/Pagination";
import usePageLanguage from "../hooks/usePageLanguage";
import "./ProductsComponent.css";
import { useShoppingCart } from "../context/ShoppingCartContext";
import type { Product } from "../interfaces/catalogProduct";
import { useAppSelector } from "../../../store/hooks";
import ProductComponent from "./ProductComponent";
import ProductsSortComponent from "./ProductsSortComponent";
import { ProductsComponent as ProductsComponent_ } from "../interfaces/base";
import {
  getComponentFromStructureMap,
  matchesEditorTarget,
} from "../utilities/componentIdentity";
import styled from "@emotion/styled";
import { useSearchParams } from "react-router-dom";
import RenderChildren from "../RenderChildren";
import { Search } from "@mui/icons-material";
import React from "react";
import { resolveCatalogProductPricing } from "../utilities/catalogProductAdapter";
import {
  filterProductsBySearchTerm,
  getVariantPriceRange,
} from "../utilities/productCatalog";

const Div = styled("div")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);
declare var API: any;

export default function ProductsComponent({
  id,
  uuid,
  attributes,
  childElements,
  childWebpageComponentRelations,
}: ProductsComponent_) {
  const editorId = useAppSelector((state) => state.textEditor?.get("id"));
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryUuidParam = searchParams.get("category");
  const searchParam = searchParams.get("s");
  const [searchVisible, setSearchVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { listableCatalogProducts, pricingNow } = useShoppingCart();
  const lang = usePageLanguage();

  const getSortPrice = (product: Product) => {
    const range = getVariantPriceRange(product);
    if (range) {
      return range.minPrice;
    }
    return resolveCatalogProductPricing(product, pricingNow).price_total;
  };

  const getProductSortName = (product: Product) => {
    const t =
      product.translations[lang as keyof typeof product.translations] ??
      product.translations.et ??
      product.translations.en;
    return (t?.name || t?.displayName || "").toUpperCase();
  };

  const [baseProducts, setBaseProducts] = useState(listableCatalogProducts);

  const [filteredProducts, setFilteredProducts] =
    useState<Product[]>(baseProducts);

  const updateSearchParams = (
    updater: (nextParams: URLSearchParams) => void
  ) => {
    const nextParams = new URLSearchParams(searchParams);
    updater(nextParams);

    const currentSerialized = searchParams.toString();
    const nextSerialized = nextParams.toString();

    if (currentSerialized !== nextSerialized) {
      setSearchParams(nextParams, { replace: true });
    }
  };

  const clearSearchParams = () =>
    updateSearchParams((nextParams) => {
      Array.from(nextParams.keys()).forEach((key) => nextParams.delete(key));
    });

  var categoryUuid = categoryUuidParam;
  var query = searchParam;

  const component = (useAppSelector((state) =>
    getComponentFromStructureMap(state.builder.structure.components, uuid ?? id)
  ) as ProductsComponent_) ?? ({
    id: typeof id === "number" ? id : -1,
    parent_id: null,
    childElements: childElements ?? [],
    childWebpageComponentRelations: childWebpageComponentRelations ?? [],
    sort_order: 0,
    type: "ProductsComponent",
    uuid: uuid ?? null,
    version: 1,
    webpage_id: 0,
    attributes,
  } as ProductsComponent_);

  const [productCategoryUuid, setProductCategoryUuid] = useState<string | null>(
    categoryUuid
  );

  useEffect(() => {
    setProductCategoryUuid(searchParams.get("category"));
  }, [searchParams]);

  const [search, setSearch] = useState<string | null>(query);

  useEffect(() => {
    setBaseProducts(listableCatalogProducts);

    // Do not reset to full list while a category/search filter is active.
    // Otherwise refreshed products.json responses cause a visible "flash".
    const hasCategoryFilter = Boolean(productCategoryUuid || categoryUuidParam);
    const hasSearchFilter = Boolean(search || searchParam);
    if (!hasCategoryFilter && !hasSearchFilter) {
      setFilteredProducts(listableCatalogProducts);
    }
  }, [
    listableCatalogProducts,
    productCategoryUuid,
    categoryUuidParam,
    search,
    searchParam,
  ]);

  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(30);
  const [sortFilter, setSortFilter] = useState<string>(
    component.attributes.default_sort
      ? component.attributes.default_sort
      : "name_asc"
  );

  const [pageCount, setPageCount] = useState(
    Math.ceil(filteredProducts.length / perPage)
  );

  useEffect(() => {
    setPageCount(Math.ceil(filteredProducts.length / perPage));
  }, [filteredProducts]);

  const handlePageChange = (event: any, value: number) => {
    setPage(value);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const languageTranslations = {
    et: {
      search: "Otsi",
    },
    en: {
      search: "Search",
    },
  };

  useEffect(() => {
    if (!searchParam) {
      setSearch(null);
    }

    if (!categoryUuidParam) {
      setProductCategoryUuid(null);
      setPage(1);
    }
  }, [searchParam, categoryUuidParam]);

  const sortedFilteredProducts = React.useMemo(() => {
    const sorted = [...filteredProducts];
    switch (sortFilter) {
      case "price_asc":
        sorted.sort((a, b) => getSortPrice(a) - getSortPrice(b));
        break;
      case "price_desc":
        sorted.sort((a, b) => getSortPrice(b) - getSortPrice(a));
        break;
      case "name_asc":
        sorted.sort((a, b) => {
          const nameA = getProductSortName(a);
          const nameB = getProductSortName(b);
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
        break;
      case "name_desc":
        sorted.sort((a, b) => {
          const nameA = getProductSortName(a);
          const nameB = getProductSortName(b);
          if (nameA < nameB) return 1;
          if (nameA > nameB) return -1;
          return 0;
        });
        break;
      default:
        break;
    }
    return sorted;
  }, [filteredProducts, sortFilter, lang, pricingNow]);

  const handleSearch = (search) => {
    let searchTerm;

    if (inputRef.current) {
      searchTerm = inputRef.current.value;
    }

    if (!searchTerm || searchTerm === "") {
      setProductCategoryUuid(null);
      setPage(1);

      setFilteredProducts(baseProducts);
      clearSearchParams();
      return;
    }

    updateSearchParams((nextParams) => {
      nextParams.delete("category");
      nextParams.set("s", searchTerm);
    });

    setProductCategoryUuid(null);
    setPage(1);

    setSearch(searchTerm);

    const matchingProducts = filterProductsBySearchTerm(
      baseProducts,
      lang,
      searchTerm
    );

    setFilteredProducts(matchingProducts);

    setPageCount(Math.ceil(matchingProducts.length / perPage));
  };

  useEffect(() => {
    if (productCategoryUuid || categoryUuidParam) {
      if (
        productCategoryUuid &&
        categoryUuidParam &&
        categoryUuidParam !== productCategoryUuid
      ) {
        updateSearchParams((nextParams) => {
          nextParams.delete("s");
          nextParams.set("category", productCategoryUuid);
        });
      }

      const activeCategoryUuid =
        productCategoryUuid ??
        (categoryUuidParam ? categoryUuidParam : null);

      const matchingProducts = baseProducts.filter((product) =>
        (product.productCategories ?? []).some(
          (productCategory) => productCategory.uuid === activeCategoryUuid
        )
      );

      setPageCount(Math.ceil(matchingProducts.length / perPage));
      setFilteredProducts(matchingProducts);
    } else if (!productCategoryUuid && !categoryUuidParam && !search && !searchParam) {
      clearSearchParams();
    }
  }, [
    productCategoryUuid,
    categoryUuidParam,
    baseProducts,
    search,
    searchParam,
    perPage,
  ]);

  useEffect(() => {
    if (search && baseProducts.length > 0) {
      updateSearchParams((nextParams) => {
        nextParams.delete("category");
        nextParams.set("s", search);
      });
      setSearch(search);

      const matchingProducts = filterProductsBySearchTerm(
        baseProducts,
        lang,
        search
      );
      setFilteredProducts(matchingProducts);

      setPageCount(Math.ceil(matchingProducts.length / perPage));

      if (inputRef.current) {
        inputRef.current.style.opacity = "1";
        inputRef.current.style.display = "inline-block";
        inputRef.current.focus();
      }

      setSearchVisible(true);

      if (search !== inputRef.current?.value && inputRef.current) {
        inputRef.current.value = search;
      }
    } else if (!productCategoryUuid && !categoryUuidParam && !search && !searchParam) {
      clearSearchParams();
    }
  }, [search, searchParam, baseProducts, lang, productCategoryUuid, categoryUuidParam]);

  const startOffset = (page - 1) * perPage;
  const endOffset = startOffset + perPage;

  useEffect(() => {
    if (productCategoryUuid) {
      const matchingProducts = baseProducts.filter((product) =>
        (product.productCategories ?? []).some(
          (productCategory) => productCategory.uuid === productCategoryUuid
        )
      );
      setPageCount(Math.ceil(matchingProducts.length / perPage));
    } else {
      setPageCount(Math.ceil(baseProducts.length / perPage));
    }
  }, [page, perPage]);

  const changeCategoryUuid = function (categoryUuid: null | string) {
    if (categoryUuid) {
      if (inputRef.current) {
        inputRef.current.value = "";
      }

      updateSearchParams((nextParams) => {
        nextParams.delete("s");
        nextParams.set("category", categoryUuid);
      });
    } else {
      clearSearchParams();
    }
    setProductCategoryUuid(categoryUuid);
    setPage(1);
  };

  useEffect(() => {
    // Update visibility based on focus
    if (inputRef.current) {
      if (document.activeElement === inputRef.current) {
        inputRef.current.style.display = "inline-block";
        inputRef.current.style.opacity = "1";
      } else {
        setSearchVisible(false);
        inputRef.current.style.display = "none";
        inputRef.current.style.opacity = "0";
      }
    } else {
      setSearchVisible(false);
    }
  }, [searchVisible]);

  useEffect(() => {
    // Reset everything, when there is no search params
    if (!searchParam && !categoryUuidParam) {
      setProductCategoryUuid(null);
      setFilteredProducts(baseProducts);
      setPage(1);
      setPageCount(Math.ceil(baseProducts.length / perPage));
    }
  }, [searchParam, categoryUuidParam, baseProducts, perPage]);

  return (
    <>
      <Div
        id={id.toString()}
        key={id}
        data-style={attributes && attributes.styles}
        className={
          attributes &&
          attributes.classes +
            (attributes.classes != "container" &&
            attributes.classes != "container-fluid"
              ? " builder-component builder-component__products"
              : "") +
            (matchesEditorTarget({ id, uuid }, editorId)
              ? " builder-component--active"
              : "")
        }
        style={{ width: "100%" }}
        data-component-type="section"
      >
        <RenderChildren
          key={id + "children"}
          childWebpageComponentRelations={childWebpageComponentRelations}
        />

        <div className="products_container">
          <div className="row">
            {component.attributes.category_visible &&
              (component.attributes.category_visible === true ||
                component.attributes.category_visible === 1) && (
                <>
                  <div className="col-lg-3 sticky">
                    <CategoriesComponent
                      id={id}
                      depthLevel={attributes.depth_level}
                      productCategoryUuid={productCategoryUuid}
                      setProductCategoryUuid={changeCategoryUuid}
                    />
                  </div>
                </>
              )}

            {component.attributes.category_visible === undefined && (
              <>
                <div className="col-lg-3 sticky">
                  <CategoriesComponent
                    id={id}
                    depthLevel={attributes.depth_level}
                    productCategoryUuid={productCategoryUuid}
                    setProductCategoryUuid={changeCategoryUuid}
                  />
                </div>
              </>
            )}

            <div
              className={
                component.attributes.category_visible === true ||
                component.attributes.category_visible === undefined ||
                component.attributes.category_visible === 1
                  ? "col-lg-9"
                  : "col-lg-12"
              }
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "end",
                  gap: "15px",
                }}
                className="margin-top-15"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "end",
                  }}
                >
                  <div
                    className={
                      searchVisible === true ? "search-container-product" : ""
                    }
                    style={{
                      display: "flex",
                      alignItems: "center",
                      height: "40px",
                    }}
                  >
                    <input
                      type="text"
                      ref={inputRef}
                      className="form-control product-search-container-input"
                      placeholder={languageTranslations[lang]?.search}
                      style={{
                        width: "100%",
                        maxWidth: "150px",
                        marginRight: "10px",
                        border: "none",
                        opacity: "0",
                        display: "none",
                      }}
                      onFocus={() => {
                        if (inputRef.current) {
                          inputRef.current.style.opacity = "1";
                          inputRef.current.style.display = "inline-block";
                        }
                      }}
                      onBlur={() => {
                        if (inputRef.current) {
                          if (inputRef.current.value) {
                            setSearchVisible(true);
                            inputRef.current.style.opacity = "1";
                            inputRef.current.style.display = "inline-block";
                          } else {
                            setSearchVisible(false);
                            inputRef.current.style.opacity = "0";
                            inputRef.current.style.display = "none";
                          }
                        }
                      }}
                      onKeyUp={(e) => {
                        if (e.key === "Enter") {
                          handleSearch(search);
                        }
                      }}
                    />

                    <Search
                      style={{ fontSize: "20px" }}
                      onClick={(e) => {
                        if (inputRef.current) {
                          setSearchVisible(true);
                          inputRef.current.style.opacity = "1";
                          inputRef.current.style.display = "inline-block";
                          inputRef.current.focus();
                        }
                        e.preventDefault();
                        e.stopPropagation();
                        handleSearch(search);
                      }}
                    />
                  </div>
                </div>
                <ProductsSortComponent
                  sortFilter={sortFilter}
                  setSortFilter={setSortFilter}
                  perPage={perPage}
                  setPerPage={setPerPage}
                />
              </div>

              <div className="card itprCard" id="productsPanel">
                <div className="card-body">
                  {sortedFilteredProducts
                    .slice(startOffset, endOffset)
                    .map((product: Product) => {
                      return (
                        <ProductComponent
                          key={product.productUuid}
                          product={product}
                        />
                      );
                    })}
                </div>
                {pageCount > 1 && (
                  <div className="card-footer">
                    <Pagination
                      className="MuiPagination-ul-center "
                      shape="rounded"
                      count={pageCount}
                      page={page}
                      onChange={handlePageChange}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Div>
    </>
  );
}
